<template>
  <section class="mb-8">
    <h2>Statistics</h2>
    <div class="mb-4">
      <div v-if="!monthlyChartData" class="animate-pulse bg-gray-700 rounded" style="height: 280px"></div>
      <template v-else>
        <canvas v-show="activeChart === 'monthly'" ref="monthlyChartRef" />
        <canvas v-show="activeChart === 'rig'" ref="rigChartRef" />
      </template>
    </div>
    <button class="btn" :disabled="chartToggleLoading" @click="toggleChart">
      {{ chartToggleLoading ? 'Loading...' : activeChart === 'monthly' ? 'Show past year by telescope' : 'Show past three years' }}
    </button>
  </section>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import Chart from 'chart.js/auto'
import { apiFetch } from '../utils/api'

Chart.defaults.color = '#fff'

const props = defineProps<{ slug: string }>()

interface ChartData {
  labels: (string | null)[]
  datasets: { label: string | number; backgroundColor: string; data: { x: string | null; y: number }[] }[]
}

const monthlyChartData = ref<ChartData | null>(null)
const rigChartData = ref<ChartData | null>(null)
const activeChart = ref<'monthly' | 'rig'>('monthly')
const chartToggleLoading = ref(false)

const monthlyChartRef = ref<HTMLCanvasElement | null>(null)
const rigChartRef = ref<HTMLCanvasElement | null>(null)
let monthlyChart: Chart | null = null
let rigChart: Chart | null = null

function buildChart(type: 'monthly' | 'rig') {
  if (type === 'monthly' && monthlyChartRef.value && !monthlyChart && monthlyChartData.value) {
    monthlyChart = new Chart(monthlyChartRef.value, {
      type: 'bar',
      data: monthlyChartData.value as any,
      options: {
        scales: { y: { beginAtZero: true } },
        plugins: { title: { display: true, text: 'Imaging Hours by Month (Past Three Years)' } },
      },
    })
  }
  if (type === 'rig' && rigChartRef.value && !rigChart && rigChartData.value) {
    rigChart = new Chart(rigChartRef.value, {
      type: 'bar',
      data: rigChartData.value as any,
      options: {
        scales: { x: { stacked: true }, y: { beginAtZero: true, stacked: true } },
        plugins: { title: { display: true, text: 'Imaging Hours by Telescope (Past Year)' } },
      },
    })
  }
}

watch(monthlyChartData, (val) => {
  if (val) nextTick(() => buildChart('monthly'))
})

watch(rigChartData, (val) => {
  if (val) nextTick(() => buildChart('rig'))
})

async function fetchChartData(type: 'monthly' | 'rig') {
  const data = await apiFetch(`/api/spa/astronomers/${props.slug}/chart_data?type=${type}`)
  if (type === 'monthly') monthlyChartData.value = data as ChartData
  else rigChartData.value = data as ChartData
}

async function toggleChart() {
  const next = activeChart.value === 'monthly' ? 'rig' : 'monthly'
  if (next === 'rig' && !rigChartData.value) {
    chartToggleLoading.value = true
    try {
      await fetchChartData('rig')
    } finally {
      chartToggleLoading.value = false
    }
  }
  activeChart.value = next
}

onMounted(() => {
  fetchChartData('monthly').catch(() => {
    // chart section stays in loading state on failure
  })
})

onUnmounted(() => {
  monthlyChart?.destroy()
  rigChart?.destroy()
})
</script>
